/**
 * FormattedText — a field of simple Markdown (`parseFormattedText`) drawn as paragraphs, bullets, numbered items,
 * bold and italic. Everything is a React element holding text nodes, so React escapes whatever was typed: there is no
 * HTML string and no `dangerouslySetInnerHTML` anywhere. The staff shell has its own copy over the same parser
 * (apps/frontend/src/app/components/patterns/FormattedText.tsx).
 */
import { Fragment } from "react";
import type {
  FormattedBlock,
  FormattedInline,
} from "@shared-core/domain/formatted-text";
import { cn } from "../primitives/utils";

function Inline({ nodes }: { nodes: readonly FormattedInline[] }) {
  return (
    <>
      {nodes.map((node, i) =>
        node.kind === "text" ? (
          <Fragment key={i}>{node.text}</Fragment>
        ) : node.kind === "bold" ? (
          <strong key={i} className="font-semibold">
            <Inline nodes={node.children} />
          </strong>
        ) : (
          <em key={i}>
            <Inline nodes={node.children} />
          </em>
        ),
      )}
    </>
  );
}

export function FormattedText({
  blocks,
  className,
}: {
  blocks: readonly FormattedBlock[];
  className?: string;
}) {
  return (
    <div className={cn("space-y-2 break-words text-sm", className)}>
      {blocks.map((block, i) => {
        if (block.kind === "paragraph")
          return (
            <p key={i}>
              {block.lines.map((line, n) => (
                <Fragment key={n}>
                  {n > 0 && <br />}
                  <Inline nodes={line} />
                </Fragment>
              ))}
            </p>
          );
        const List = block.kind === "bullets" ? "ul" : "ol";
        return (
          <List
            key={i}
            className={cn(
              "space-y-0.5 pl-5",
              block.kind === "bullets" ? "list-disc" : "list-decimal",
            )}
          >
            {block.items.map((item, n) => (
              <li key={n}>
                <Inline nodes={item} />
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}
